'use client';

import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';

import { getSeoResolveReq } from '@/features/seo-listing/api';
import type { SeoResolveData } from '@/features/seo-listing/types';
import { applySeoResolveDocumentMeta } from '@/features/seo-listing/utils';
import { isPermalinkPath } from '@/features/seo-listing/consts';
import type { FilterAction, FilterState } from '../store';
import type { ListingRestoreSnapshot } from '../types';
import {
  buildFilterReloadContext,
  type FilterReloadContext,
} from '@/features/fanfiction-grid/utils';
import {
  clearListingRestoreSnapshot,
  createFilterStateFromRestoreSnapshot,
  getBrowserPathname,
} from '../utils';
import { useListingRestoreOnBack } from './useListingRestoreOnBack';

interface UseListingRestoreNavigationParams {
  basePath: string;
  listingRestoreSnapshot: ListingRestoreSnapshot | null;
  filterStateRef: RefObject<FilterState>;
  seoPageTitleRef: RefObject<string | null>;
  seoPageRef: RefObject<SeoResolveData | null>;
  isFirstRenderRef: RefObject<boolean>;
  isPopstateNavigationRef: RefObject<boolean>;
  dispatch: Dispatch<FilterAction>;
  setSeoPage: Dispatch<SetStateAction<SeoResolveData | null>>;
  setSeoPageTitle: Dispatch<SetStateAction<string | null>>;
  setHasLoaded: Dispatch<SetStateAction<boolean>>;
  setIsRefetching: Dispatch<SetStateAction<boolean>>;
  syncRelatedFilterOptions: (context: FilterReloadContext) => void;
}

/**
 * Soft-Back listing restore: chrome + click capture, apply snapshot filters
 * after hydration (during render), then re-resolve SEO and reload facets.
 */
export const useListingRestoreNavigation = ({
  basePath,
  listingRestoreSnapshot,
  filterStateRef,
  seoPageTitleRef,
  seoPageRef,
  isFirstRenderRef,
  isPopstateNavigationRef,
  dispatch,
  setSeoPage,
  setSeoPageTitle,
  setHasLoaded,
  setIsRefetching,
  syncRelatedFilterOptions,
}: UseListingRestoreNavigationParams): void => {
  const [appliedRestoreId, setAppliedRestoreId] = useState<string | null>(null);

  useListingRestoreOnBack({
    basePath,
    listingRestoreSnapshot,
    filterStateRef,
    seoPageTitleRef,
    seoPageRef,
  });

  // Apply Back-restore when useSyncExternalStore exposes the session snapshot
  // after hydration (null on server/first paint). Adjust state during render —
  // not in an effect — to avoid flushSync / set-state-in-effect cascades.
  if (listingRestoreSnapshot && appliedRestoreId !== listingRestoreSnapshot.restoreId) {
    setAppliedRestoreId(listingRestoreSnapshot.restoreId);
    dispatch({
      type: 'SYNC_FROM_URL',
      payload: createFilterStateFromRestoreSnapshot(listingRestoreSnapshot),
    });
    if (listingRestoreSnapshot.seoPage) {
      setSeoPage(listingRestoreSnapshot.seoPage);
      setSeoPageTitle(listingRestoreSnapshot.seoPage.title ?? null);
    } else if (listingRestoreSnapshot.seoPageTitle) {
      setSeoPageTitle(listingRestoreSnapshot.seoPageTitle);
    }
    setHasLoaded(false);
    setIsRefetching(true);
  }
  if (!listingRestoreSnapshot && appliedRestoreId !== null) {
    setAppliedRestoreId(null);
  }

  useLayoutEffect(() => {
    if (!listingRestoreSnapshot) {
      return;
    }
    isFirstRenderRef.current = false;
    isPopstateNavigationRef.current = true;
  }, [isFirstRenderRef, isPopstateNavigationRef, listingRestoreSnapshot]);

  // After Back, Next may remount the catalog RSC tree while history (and listing
  // restore) already point at a pushState permalink. Always re-resolve SEO for
  // that path so H1/chrome match the restored URL even if the snapshot had no
  // seoPage payload.
  useEffect(() => {
    if (!listingRestoreSnapshot) {
      return;
    }
    const pathFromSnapshot = listingRestoreSnapshot.href.split('?')[0] || '';
    const browserPath = getBrowserPathname();
    const path = isPermalinkPath(browserPath) ? browserPath : pathFromSnapshot;
    if (!isPermalinkPath(path)) {
      clearListingRestoreSnapshot();
      return;
    }
    let isCancelled = false;
    void getSeoResolveReq({ path }).then((result) => {
      if (isCancelled) {
        return;
      }
      if (result.status === 'ok') {
        setSeoPage(result.data);
        setSeoPageTitle(result.data.title ?? null);
        applySeoResolveDocumentMeta(result.data);
      }
      clearListingRestoreSnapshot();
    });
    return () => {
      isCancelled = true;
    };
  }, [listingRestoreSnapshot, setSeoPage, setSeoPageTitle]);

  useEffect(() => {
    if (!listingRestoreSnapshot) {
      return;
    }

    syncRelatedFilterOptions(
      buildFilterReloadContext(getBrowserPathname(), listingRestoreSnapshot.filters),
    );
  }, [listingRestoreSnapshot, syncRelatedFilterOptions]);
};
